import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  List,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";
import { grey } from "@mui/material/colors";
import { AnimatePresence } from "framer-motion";
import { setShowCartDrawer } from "../../../../store/actions/newOrderSlice";
import ProductListItem from "./ProductListItem";
import PaymentInfoPanel from "../financial/PaymentInfoPanel";

/**
 * CartDrawer - Right-side drawer showing full cart contents
 *
 * Features:
 * - Opens when showCartDrawer is true
 * - Rounded left corners (16px 0 0 16px)
 * - Full width on mobile (xs: 100%, sm+: 450px max)
 * - Scrollable cart items area
 * - Sticky PaymentInfoPanel at bottom
 * - Empty state with icon
 *
 * Props:
 * - submitOrder: Callback for PaymentInfoPanel
 */
const CartDrawer = ({ submitOrder }) => {
  const dispatch = useDispatch();
  const showCartDrawer = useSelector((state) => state.newOrder.showCartDrawer);
  const cartProducts = useSelector((state) => state.newOrder.cartProducts);

  const handleClose = () => {
    dispatch(setShowCartDrawer(false));
  };

  return (
    <Drawer
      anchor="right"
      open={showCartDrawer}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 450 },
          maxWidth: "100%",
          borderRadius: { xs: 0, sm: "16px 0 0 16px" },
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Stack
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: grey[50],
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Carrinho
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Fechar carrinho"
            sx={{
              "&:hover": {
                bgcolor: grey[200],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Cart Content (scrollable) */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            bgcolor: grey[100],
          }}
        >
          {cartProducts && cartProducts.length > 0 ? (
            <List sx={{ p: 0 }}>
              <AnimatePresence mode="popLayout">
                {cartProducts.map((product, index) => (
                  <ProductListItem
                    key={product.product_identifier}
                    product={product}
                    isFirst={index === 0}
                  />
                ))}
              </AnimatePresence>
            </List>
          ) : (
            // Empty state
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
                p: 4,
              }}
            >
              <RemoveShoppingCartOutlinedIcon
                sx={{
                  fontSize: 64,
                  color: grey[400],
                }}
              />
              <Typography variant="h6" color="text.secondary" align="center">
                Carrinho vazio
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Adicione produtos para começar seu pedido
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Payment Panel (sticky at bottom) */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#ffffff",
            borderTop: `1px solid ${grey[200]}`,
          }}
        >
          <PaymentInfoPanel submitOrder={submitOrder} />
        </Box>
      </Stack>
    </Drawer>
  );
};

export default CartDrawer;
